import type { PriceTagMarker } from "./PriceTagMap";
import { toCoordinate } from "../../../../../util/mapEmbed";

/** The room being looked at, as the map needs it. */
export interface OwnRoomPin {
    id: string;
    label: string;
    /** Straight from the API: an address coordinate may arrive as a number or a string. */
    lat: unknown;
    lng: unknown;
}

// One definition of "a usable coordinate", shared with the static maps.
export { toCoordinate };

/**
 * The map's pins take the SEARCH RESULTS. On a room page reached without them
 * (a direct link, or a search that found nothing) there were no pins, and a
 * Google map without a pin has no centre: it rendered as a grey area.
 *
 * So the room being looked at is always on its own map: when no pin is the
 * selected one, the room is added from its own address and selected.
 */
export const withOwnRoom = (markers: PriceTagMarker[], room: OwnRoomPin | null | undefined): PriceTagMarker[] => {
    if (!room) return markers;
    if (markers.some((marker) => marker.isSelected || marker.id === room.id)) return markers;
    const lat = toCoordinate(room.lat);
    const lng = toCoordinate(room.lng);
    if (lat === null || lng === null) return markers;
    return [...markers, { id: room.id, lat, lng, label: room.label, isSelected: true }];
};

/** A pin the map can draw — the one definition, used by `PriceTagMap` too. */
export const isPinned = (marker: PriceTagMarker): boolean =>
    Number.isFinite(marker.lat) && Number.isFinite(marker.lng);

/**
 * Whether the interactive map has anything to centre on. Without a single
 * pin it renders grey — so a room whose location has an address but no pin
 * gets the embedded map of its address instead (PO, 2026-10-19: "besser als
 * gar keine Karte"). The same test `PriceTagMap` applies before it draws.
 */
export const hasPin = (markers: PriceTagMarker[]): boolean =>
    markers.some(isPinned);

/**
 * Whether THIS room is on the map — the test for a room page's "Lage" map.
 * The room counts as pinned through its own coordinates, or through the
 * selected search result (the room being looked at, as `withOwnRoom` reads
 * it). Pins of other results do not count: a map centred on the neighbours
 * would put the room in the wrong place.
 */
export const roomIsPinned = (markers: PriceTagMarker[], room: OwnRoomPin | null | undefined): boolean => {
    if (!room) return false;
    if (toCoordinate(room.lat) !== null && toCoordinate(room.lng) !== null) return true;
    return markers.some((marker) => (marker.isSelected || marker.id === room.id) && isPinned(marker));
};
